// Pure helpers for the Course Approvals "Instructor Sign-ups" tab. A "group" is one upcoming slot (opening) with the
// instructors who signed up for it, exactly as GET /api/v1/instructor-signups returns it.

const DAY = 864e5;
const RECENT_MS = DAY; // a slot filled within the last day stays in Pending so the decision can be undone

const ROLE_LABELS = { LEAD_INSTRUCTOR: "Lead instructor", ASSISTANT_INSTRUCTOR: "Assistant instructor", TA: "TA" };
const ROLE_ORDER = { LEAD_INSTRUCTOR: 0, ASSISTANT_INSTRUCTOR: 1, TA: 2 };
const STATUS_ORDER = { APPROVED: 0, PENDING: 1, UNAPPROVED: 2 };
const AVATAR_COLORS = ["#6a5fc1", "#b4441b", "#3b5bdb", "#2b8a3e", "#7048e8", "#c23d68", "#0277bd"];

export function roleLabel(role) {
  if (!role) return "";
  if (ROLE_LABELS[role]) return ROLE_LABELS[role];
  return String(role).toLowerCase().split("_").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}

/** Always names the session ("Session 2"), adding its name when that says something more. */
export function sessionLabel(group) {
  const numbered = group.sessionNumber ? `Session ${group.sessionNumber}` : "";
  const name = (group.sessionName || "").trim();
  if (numbered && name && name !== numbered) return `${numbered} · ${name}`;
  return numbered || name || "Session";
}

const parseDay = (iso) => {
  const [y, m, d] = String(iso).slice(0, 10).split("-").map(Number);
  return new Date(y, m - 1, d);
};

export function daysUntil(sessionDate, now) {
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  return Math.round((parseDay(sessionDate) - today) / DAY);
}

export const isOpen = (group) => group.status === "OPEN";
export const isUrgent = (group, now) => isOpen(group) && daysUntil(group.sessionDate, now) < 7;

export function isRecentlyFilled(group, now) {
  return group.status === "FILLED" && Boolean(group.filledAt) && now - new Date(group.filledAt).getTime() < RECENT_MS;
}
export const inPendingView = (group, now) => isOpen(group) || isRecentlyFilled(group, now);
export const inDecidedView = (group, now) => group.status === "FILLED" && !isRecentlyFilled(group, now);

export function orderApplicants(group) {
  return [...group.applicants].sort((a, b) =>
    (STATUS_ORDER[a.status] - STATUS_ORDER[b.status]) || String(a.requestedAt).localeCompare(String(b.requestedAt)));
}

export function earliestPending(group) {
  const times = group.applicants.filter((a) => a.status === "PENDING").map((a) => a.requestedAt).filter(Boolean).sort();
  return times.length ? times[0] : null;
}

export function agoLabel(iso, now) {
  if (!iso) return "";
  const minutes = Math.max(1, Math.round((now - new Date(iso).getTime()) / 6e4));
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hr`;
  const days = Math.round(hours / 24);
  return `${days} ${days === 1 ? "day" : "days"}`;
}

export const formatDay = (sessionDate) =>
  parseDay(sessionDate).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });

export function formatStamp(iso) {
  const d = new Date(iso);
  return `${d.toLocaleDateString("en-US", { month: "short", day: "numeric" })}, ${d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`;
}

export function initials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return parts.map((p) => p.charAt(0)).join("").slice(0, 2).toUpperCase();
}

export function avatarColor(name = "") {
  let h = 0;
  for (let i = 0; i < name.length; i += 1) h = (name.charCodeAt(i) + ((h << 5) - h)) | 0;
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}

export const isBlocked = (applicant) => Boolean(applicant.blockerKind);

/** Longest comment the server accepts on a rejection. */
export const NOTE_MAX = 500;

/**
 * Whole-class clusters: one instructor with pending sign-ups for two or more open sessions of the same course and role.
 * Each cluster lists its sessions earliest first; `approvable` are the ones Approve would not refuse right now.
 */
export function buildClusters(groups) {
  const byKey = new Map();
  groups.filter(isOpen).forEach((group) => {
    group.applicants.filter((a) => a.status === "PENDING").forEach((applicant) => {
      const key = `${applicant.instructorId}|${group.classId}|${group.teachingRole}`;
      if (!byKey.has(key)) {
        byKey.set(key, {
          key, instructorId: applicant.instructorId, instructorName: applicant.instructorName, classId: group.classId,
          courseTitle: group.courseTitle, courseNumber: group.courseNumber, teachingRole: group.teachingRole, items: [],
        });
      }
      byKey.get(key).items.push({ signupId: applicant.id, group, applicant });
    });
  });
  return [...byKey.values()]
    .filter((c) => c.items.length >= 2)
    .map((c) => {
      const items = [...c.items].sort((a, b) => byDate(a.group, b.group));
      return { ...c, items, approvable: items.filter((i) => !isBlocked(i.applicant)) };
    })
    .sort((a, b) => byDate(a.items[0].group, b.items[0].group));
}

/** Empty string when Approve is allowed; otherwise the reason it is disabled (also the button's tooltip). */
export function approveDisabledReason(applicant, group) {
  if (applicant.status === "APPROVED") return "";
  if (group.status === "FILLED") {
    const filler = group.applicants.find((a) => a.instructorId === group.assignedInstructorId);
    return `Filled by ${filler?.instructorName || "another instructor"}. Unapprove them to choose someone else.`;
  }
  if (applicant.blockerKind === "CONFLICT") return "Blocked by a schedule conflict";
  if (applicant.blockerKind === "AHA") return "Blocked: AHA instructor ID missing";
  return "";
}

export function filterGroups(groups, { sub, attention, course, role, query }, now) {
  const q = (query || "").trim().toLowerCase();
  return groups.filter((g) => {
    if (sub === "pending" && !inPendingView(g, now)) return false;
    if (sub === "decided" && !inDecidedView(g, now)) return false;
    if (attention && !isUrgent(g, now)) return false;
    if (course && g.courseTitle !== course) return false;
    if (role && g.teachingRole !== role) return false;
    if (q) {
      const hay = [g.courseTitle, g.courseNumber, ...g.applicants.map((a) => a.instructorName)].join(" ").toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
}

const byDate = (a, b) => String(a.sessionDate).localeCompare(String(b.sessionDate));

export function sortGroups(groups, sort) {
  const sorters = {
    soon: byDate,
    wait: (a, b) => String(earliestPending(a) || "9").localeCompare(String(earliestPending(b) || "9")) || byDate(a, b),
    course: (a, b) => String(a.courseTitle).localeCompare(String(b.courseTitle)) || byDate(a, b),
    role: (a, b) => ((ROLE_ORDER[a.teachingRole] ?? 9) - (ROLE_ORDER[b.teachingRole] ?? 9)) || byDate(a, b),
  };
  return [...groups].sort(sorters[sort] || byDate);
}

export function countGroups(groups, now) {
  return {
    pending: groups.filter((g) => inPendingView(g, now)).length,
    decided: groups.filter((g) => inDecidedView(g, now)).length,
    all: groups.length,
    attention: groups.filter((g) => isUrgent(g, now)).length,
    openSlots: groups.filter(isOpen).length,
  };
}
